import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { BaseService } from './base.service';
import {
  LoginDto,
  RequestOtpDto,
  VerifyOtpDto,
  RegisterDto,
  SiInsurerRegisterDto,
  CheckOtpDto,
  CheckPasswordOtpDto,
  RequestPasswordResetDto,
  ChangePasswordDto,
  ChangeCurrentPasswordDto,
} from '../interfaces/auth.interface';
import { User } from '../interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_DATA_KEY = 'user_data';
  private readonly RESET_PASSWORD_KEY = 'reset_password_token';
  private readonly SESSION_ID_KEY = 'session_id';
  private platformId = inject(PLATFORM_ID);

  constructor(http: HttpClient) {
    super(http);
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // Gestion du token
  setToken(token: string): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.TOKEN_KEY, token);
    }
  }

  getToken(): string | null {
    if (this.isBrowser()) {
      return localStorage.getItem(this.TOKEN_KEY);
    }
    return null;
  }

  setRefreshToken(token: string): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
    }
  }

  getRefreshToken(): string | null {
    if (this.isBrowser()) {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    }
    return null;
  }

  setUserData(userData: any): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
    }
  }

  getUserData(): any {
    if (this.isBrowser()) {
      const userData = localStorage.getItem(this.USER_DATA_KEY);
      return userData ? JSON.parse(userData) : null;
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Vérifier si le token est expiré
    return !this.isTokenExpired(token);
  }

  isTokenExpired(token: string): boolean {
    try {
      // Décoder le token JWT pour vérifier l'expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Erreur lors de la vérification du token:', error);
      return true; // Considérer comme expiré en cas d'erreur
    }
  }

  isRefreshTokenExpired(): boolean {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return true;

    try {
      // Décoder le refresh token JWT pour vérifier l'expiration
      const payload = JSON.parse(atob(refreshToken.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Erreur lors de la vérification du refresh token:', error);
      return true; // Considérer comme expiré en cas d'erreur
    }
  }

  // Requête de logout vers l'API
  logoutRequest(): Observable<any> {
    const sessionId = this.getSessionId();
    const body: { sessionId?: string } = {};
    
    if (sessionId) {
      body.sessionId = sessionId;
    }

    return of({ success: true });

    // return this.http.post(`${this.baseUrl}/auth/logout`, body, {
    //   headers: this.getHeaders(),
    // });
  }

  logout(): void {
    // Récupérer le sessionId avant de supprimer les données
    const sessionId = this.getSessionId();
    
    // Envoyer la requête de logout à l'API (fire and forget)
    if (sessionId) {
      this.logoutRequest().subscribe({
        next: () => {
          console.log('✅ Logout réussi sur le serveur');
        },
        error: (error) => {
          console.error('❌ Erreur lors du logout sur le serveur:', error);
          // Continuer quand même avec le logout local même en cas d'erreur
        },
      });
    }

    // Supprimer les données du localStorage
    // if (this.isBrowser()) {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
    localStorage.removeItem(this.RESET_PASSWORD_KEY);
    localStorage.removeItem(this.SESSION_ID_KEY);
    // }
  }

  // Gestion du token de reset_password
  setResetPasswordToken(token: string): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.RESET_PASSWORD_KEY, token);
    }
  }

  getResetPasswordToken(): string | null {
    if (this.isBrowser()) {
      return localStorage.getItem(this.RESET_PASSWORD_KEY);
    }
    return null;
  }

  clearResetPasswordToken(): void {
    if (this.isBrowser()) {
      localStorage.removeItem(this.RESET_PASSWORD_KEY);
    }
  }

  // Gestion du sessionId
  setSessionId(sessionId: string): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.SESSION_ID_KEY, sessionId);
    }
  }

  getSessionId(): string | null {
    if (this.isBrowser()) {
      return localStorage.getItem(this.SESSION_ID_KEY);
    }
    return null;
  }

  // Authentification de base
  authenticateUser(): Observable<User> {
    return this.http.get<User>(`${this.baseUrl}/auth`, {
      headers: this.getHeaders(),
    });
  }

  // Connexion
  login(loginData: LoginDto): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login`, loginData, {
      headers: this.getHeaders(),
    });
  }

  refreshToken() {
    const refreshToken: string = this.getRefreshToken() || '';
    return this.http.post(
      `${this.baseUrl}/auth/refresh`,
      { refreshToken },
      {
        headers: this.getHeaders(),
      }
    );
  }

  /**
   * Vérifie si le token doit être rafraîchi et le fait automatiquement
   */
  async checkAndRefreshToken(): Promise<boolean> {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();

    // Si pas de token, pas authentifié
    if (!token) return false;

    // Si le token n'est pas expiré, tout va bien
    if (!this.isTokenExpired(token)) return true;

    // Si le refresh token est expiré, déconnexion nécessaire
    if (!refreshToken || this.isRefreshTokenExpired()) {
      console.log('🔐 Refresh token expiré, déconnexion nécessaire');
      this.logout();
      return false;
    }

    try {
      console.log('🔐 Token expiré, tentative de refresh automatique');
      const response: any = await this.refreshToken().toPromise();

      if (response && response.accessToken) {
        this.setToken(response.accessToken);
        if (response.refreshToken) {
          this.setRefreshToken(response.refreshToken);
        }
        console.log('🔐 Token rafraîchi avec succès');
        return true;
      }
    } catch (error) {
      console.error('🔐 Échec du refresh token:', error);
      this.logout();
      return false;
    }

    return false;
  }

  // Demande d'OTP
  requestOtp(otpData: RequestOtpDto): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/otp/request`, otpData, {
      headers: this.getHeaders(),
    });
  }

  // Vérification d'OTP
  verifyOtp(verifyData: VerifyOtpDto): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/otp/verify`, verifyData, {
      headers: this.getHeaders(),
    });
  }

  // Inscription standard
  register(registerData: RegisterDto): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/register`, registerData, {
      headers: this.getHeaders(),
    });
  }

  // Inscription depuis SI Insurer
  registerFromSiInsurer(siInsurerData: SiInsurerRegisterDto): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/auth/register/si-insurer`,
      siInsurerData,
      { headers: this.getHeaders() }
    );
  }

  // Vérification si OTP est valide
  checkIfOtpIsValid(checkData: CheckOtpDto): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/auth/password/token/check`,
      checkData,
      { headers: this.getHeaders() }
    );
  }

  // Vérification OTP mot de passe
  checkPasswordOtp(checkData: CheckPasswordOtpDto): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/auth/password/otp/check`,
      checkData,
      { headers: this.getHeaders() }
    );
  }

  // Demande de réinitialisation de mot de passe
  requestPasswordReset(resetData: RequestPasswordResetDto): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/auth/password/reset/request`,
      resetData,
      { headers: this.getHeaders() }
    );
  }

  // Changement de mot de passe
  changePassword(changeData: ChangePasswordDto): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/password/change`, changeData, {
      headers: this.getHeaders(),
    });
  }

  changeCurrentPassword(changeData: ChangeCurrentPasswordDto): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/auth/password/change/with-current`,
      changeData,
      { headers: this.getHeaders() }
    );
  }
}
